/**
 * Live damage compositing on baked demolition maps.
 * Maps stay the undamaged hero look; the 3D field + decal atlas paint on top.
 */
import type { ProfileId } from '../../services/destructionProfiles';
import type { SurfaceFace } from './SurfaceDefinition';
import { SURFACE_MICRO_GLSL } from './SurfaceMicroDetail';
import { SURFACE_NOISE_GLSL } from './SurfaceNoise';
import { SURFACE_TRIPLANAR_GLSL } from './TriplanarProjection';
import { DAMAGE_DECAL_ATLAS_GLSL } from './DamageDecalAtlas';

export interface BakedDamageHookOpts {
  profileId?: ProfileId;
  face?: SurfaceFace;
  /** Interior rubble settles slightly with gDamageAge. */
  interiorAgeSettle?: boolean;
  brickTextureScale?: number;
  brickSeed?: number;
  brickMortarWidth?: number;
  brickCourseJitter?: number;
  stoneBlockIrregularity?: number;
  textureScale?: number;
  seed?: number;
}

const BRICK_GRID_GLSL = /* glsl */ `
uniform float uBakedBrickScale;
uniform float uBakedBrickSeed;
uniform float uBakedBrickMortarWidth;
uniform float uBakedBrickCourseJitter;
float gBrickEdgeX = 0.5;
float gBrickEdgeY = 0.5;
float gBrickRow = 0.0;
float gBrickCol = 0.0;
float gBrickFaceX = 0.0;
float gBrickFaceY = 0.0;
float gBrickMortar = 0.0;
float gBrickChip = 0.0;

void bakedBrickGrid(vec3 restPos, vec3 restNormal) {
  vec3 n = normalize(restNormal);
  vec3 w = surfaceTriplanarWeights(n);
  vec2 front = restPos.xy;
  vec2 side = restPos.zy;
  vec2 top = restPos.xz;
  float scale = max(uBakedBrickScale, 0.05);
  float fp = surfacePixelFootprint(restPos);
  vec2 uv = (front * w.z + side * w.x + top * w.y) / scale;
  float course = uv.y / 0.46;
  float row = floor(course);
  float stagger = mod(row, 2.0) * 0.5;
  float rowJitter = (surfaceHash_(vec3(row, 7.0, 13.0), uBakedBrickSeed * 13.17) - 0.5) * uBakedBrickCourseJitter;
  float col = floor(uv.x / 0.98 + stagger + rowJitter);
  float brickX = fract(uv.x / 0.98 + stagger + rowJitter);
  float brickY = fract(course);
  gBrickEdgeX = min(brickX, 1.0 - brickX);
  gBrickEdgeY = min(brickY, 1.0 - brickY);
  gBrickRow = row;
  gBrickCol = col;
  gBrickFaceX = brickX;
  gBrickFaceY = brickY;
  float metersPerEdge = 0.46 * scale;
  float fpInEdgeUnits = fp / max(metersPerEdge, 1e-6);
  float mortarHalfW = max(0.025, fpInEdgeUnits * 1.0);
  gBrickMortar = 1.0 - smoothstep(uBakedBrickMortarWidth - mortarHalfW, uBakedBrickMortarWidth + mortarHalfW, min(gBrickEdgeX, gBrickEdgeY));
  gBrickChip = 0.0;
}
`;

const STONE_GRID_GLSL = /* glsl */ `
uniform float uBakedStoneIrregularity;
float gStoneEdgeX = 0.5;
float gStoneEdgeY = 0.5;
float gStoneRow = 0.0;
float gStoneCol = 0.0;
float gStoneFaceX = 0.5;
float gStoneFaceY = 0.5;
float gStoneJoint = 0.0;
float gStoneBlockW = 1.05;
float gStoneCourseH = 0.58;

void bakedStoneGrid(vec3 restPos, vec3 restNormal) {
  vec3 n = normalize(restNormal);
  vec3 w = surfaceTriplanarWeights(n);
  vec2 front = restPos.xy;
  vec2 side = restPos.zy;
  vec2 top = restPos.xz;
  float scale = max(uBakedTextureScale, 0.05);
  float fp = surfacePixelFootprint(restPos);
  vec2 uv = (front * w.z + side * w.x + top * w.y) / scale;
  float irr = clamp(uBakedStoneIrregularity, 0.0, 1.0);
  float waveWl = scale * 0.22;
  float waveFade = surfaceOctaveFade(waveWl, fp);
  float waveX = surfaceNoiseBL(vec3(uv.x * 1.7, uv.y * 0.55, 0.0) + vec3(5.0, 1.0, 9.0), uBakedSeed * 13.17, waveWl, fp);
  float waveY = surfaceNoiseBL(vec3(uv.x * 0.6, uv.y * 1.9, 0.0) + vec3(2.0, 11.0, 4.0), uBakedSeed * 13.17, waveWl, fp);
  uv.x += (waveX - 0.5) * 0.045 * irr * waveFade;
  uv.y += (waveY - 0.5) * 0.028 * irr * waveFade;

  float meanH = 0.58;
  float meanW = 1.05;
  float yCell = uv.y / meanH;
  float yBase = floor(yCell);
  float yBest = 100.0;
  float ySecond = 100.0;
  float row = yBase;
  float yFeatN = yBase;
  float yFeatS = yBase + 1.0;
  for (int j = 0; j < 7; j++) {
    float c = yBase + float(j) - 3.0;
    float hh = surfaceHash_(vec3(c, 3.0, 11.0), uBakedSeed * 13.17);
    float skip = surfaceHash_(vec3(c, 4.0, 13.0), uBakedSeed * 13.17);
    if (skip <= 0.86) {
      float jit = mix(0.18, 0.82, hh);
      jit += (surfaceHash_(vec3(c, 8.0, 2.0), uBakedSeed * 13.17) - 0.5) * 0.14 * irr;
      float feat = c + jit;
      float d = abs(yCell - feat);
      if (d < yBest) {
        ySecond = yBest;
        yFeatS = yFeatN;
        yBest = d;
        yFeatN = feat;
        row = c;
      } else if (d < ySecond) {
        ySecond = d;
        yFeatS = feat;
      }
    }
  }
  float courseH = max(yBest + ySecond, 0.30) * meanH;
  float edgeY = clamp((ySecond - yBest) * 0.5 / max(yBest + ySecond, 1e-4), 0.0, 0.5);

  float stagger = mod(abs(row), 2.0) * 0.22;
  float xCell = uv.x / meanW - stagger;
  float xBase = floor(xCell);
  float xBest = 100.0;
  float xSecond = 100.0;
  float col = xBase;
  float xFeatN = xBase;
  float xFeatS = xBase + 1.0;
  for (int i = 0; i < 9; i++) {
    float c = xBase + float(i) - 4.0;
    float hh = surfaceHash_(vec3(c, row, 5.0), uBakedSeed * 13.17);
    float h2 = surfaceHash_(vec3(c, row, 19.0), uBakedSeed * 13.17);
    if (h2 <= 0.84) {
      float jit = mix(0.16, 0.84, hh);
      jit += (h2 - 0.5) * 0.16 * irr;
      float feat = c + jit;
      float d = abs(xCell - feat);
      if (d < xBest) {
        xSecond = xBest;
        xFeatS = xFeatN;
        xBest = d;
        xFeatN = feat;
        col = c;
      } else if (d < xSecond) {
        xSecond = d;
        xFeatS = feat;
      }
    }
  }
  float blockW = max(xBest + xSecond, 0.22) * meanW;
  float edgeX = clamp((xSecond - xBest) * 0.5 / max(xBest + xSecond, 1e-4), 0.0, 0.5);
  gStoneRow = row;
  gStoneCol = col;
  gStoneEdgeX = edgeX;
  gStoneEdgeY = edgeY;
  gStoneBlockW = max(blockW, 0.22);
  gStoneCourseH = max(courseH, 0.22);
  gStoneFaceX = clamp((xCell - min(xFeatN, xFeatS)) / max(abs(xFeatN - xFeatS), 1e-4), 0.0, 1.0);
  gStoneFaceY = clamp((yCell - min(yFeatN, yFeatS)) / max(abs(yFeatN - yFeatS), 1e-4), 0.0, 1.0);

  float metersPerEdge = meanH * scale;
  float dX = edgeX * blockW * scale / max(metersPerEdge, 1e-6);
  float dY = edgeY * courseH * scale / max(metersPerEdge, 1e-6);
  float edgeMin = min(dX, dY);
  float jointW = 0.050;
  float fpEdge = fp / max(metersPerEdge, 1e-6);
  float jointHalfW = max(0.055, fpEdge * 1.4);
  float joint = 1.0 - smoothstep(jointW - jointHalfW, jointW + jointHalfW, edgeMin);
  float grooveWidthMeters = 3.0 * jointW * metersPerEdge;
  float grooveFade = surfaceOctaveFade(grooveWidthMeters, fp);
  float jointVis = clamp(jointW / max(fpEdge * 0.8, 1e-6), 0.0, 1.0);
  gStoneJoint = joint * grooveFade * jointVis;
}
`;

export function bakedDamageFragmentPrelude(opts: BakedDamageHookOpts): string {
  const exterior = opts.face !== 'interior';
  const brick = opts.profileId === 'brick' && exterior;
  const stone = opts.profileId === 'stone' && exterior;
  const atlas = exterior && (
    opts.profileId === 'concrete'
    || opts.profileId === 'brick'
    || opts.profileId === 'stone'
    || opts.profileId === 'wood'
  );
  const needNoise = atlas || brick || stone;
  return `
#ifndef U_BAKE_DOWN_DECALS
#define U_BAKE_DOWN_DECALS
uniform float uBakeDownDecals;
#endif
${needNoise ? SURFACE_TRIPLANAR_GLSL : ''}
${needNoise ? SURFACE_NOISE_GLSL : ''}
${needNoise ? SURFACE_MICRO_GLSL : ''}
${atlas ? DAMAGE_DECAL_ATLAS_GLSL : ''}
${brick ? BRICK_GRID_GLSL : ''}
${stone ? STONE_GRID_GLSL : ''}
`;
}

export function bakedDamageColorSnippet(opts: BakedDamageHookOpts): string {
  const face = opts.face ?? 'exterior';
  const id = opts.profileId;
  const settle =
    face === 'interior' && opts.interiorAgeSettle !== false
      ? `
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.86, 0.84, 0.80), gDamageAge * 0.22);
float bakedSat = dot(diffuseColor.rgb, vec3(0.299, 0.587, 0.114));
diffuseColor.rgb = mix(diffuseColor.rgb, vec3(bakedSat), gDamageAge * 0.12);
`
      : '';

  if (face === 'interior' || !id) {
    return `#include <color_fragment>
#ifdef USE_DAMAGE_FIELD
fillDamageGlobals(vBakedRestPosition, normalize(vBakedRestNormal));
float bakedFresh = 1.0 - gDamageAge * 0.32;
float bakeDownLive = 1.0 - uBakeDownDecals;
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.55, 0.52, 0.48), gDamageBlotch * mix(0.28, 0.58, clamp(gDamage, 0.0, 1.0)) * bakedFresh * bakeDownLive);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * 0.22, gDamageCrack * bakedFresh * bakeDownLive);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.16, 0.14, 0.12), gDamageCore * 0.7 * bakedFresh * bakeDownLive);
${settle}
#endif
`;
  }

  if (id === 'brick') {
    return `#include <color_fragment>
#ifdef USE_DAMAGE_FIELD
fillDamageGlobals(vBakedRestPosition, normalize(vBakedRestNormal));
float bakedFresh = 1.0 - gDamageAge * 0.32;
float bakeDownLive = 1.0 - uBakeDownDecals;
bakedBrickGrid(vBakedRestPosition, normalize(vBakedRestNormal));
#ifdef USE_DAMAGE_DECAL_ATLAS
sampleBrickDamageDecals(
  vBakedRestPosition,
  normalize(vBakedRestNormal),
  uBakedBrickScale,
  gBrickMortar,
  gBrickChip,
  gBrickEdgeX,
  gBrickEdgeY,
  gBrickRow,
  gBrickCol,
  gBrickFaceX,
  gBrickFaceY
);
vec3 brickInteriorClay = vec3(0.82, 0.34, 0.16);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.90, 0.86, 0.82), gDamageBlotch * 0.04 * bakedFresh * bakeDownLive);
float brickFp = surfacePixelFootprint(vBakedRestPosition);
float brickMeters = max(uBakedBrickScale, 0.05);
vec3 brickClayP = vBakedRestPosition * (1.0 / brickMeters);
float gBrickClayGrain = surfaceNoiseBL(brickClayP * 6.0 + vec3(3.0, 8.0, 1.0), uBakedBrickSeed * 21.7, brickMeters / 6.0, brickFp) * 0.55
  + surfaceNoiseBL(brickClayP * 12.0 + vec3(9.0, 2.0, 5.0), uBakedBrickSeed * 21.7, brickMeters / 12.0, brickFp) * 0.30;
vec3 brickSpallMean = mix(brickInteriorClay * 1.05, brickInteriorClay * 0.78, clamp(gDecalDepth, 0.0, 1.0));
brickSpallMean *= mix(0.90, 1.18, gBrickClayGrain);
brickSpallMean = mix(brickSpallMean, brickSpallMean * vec3(0.84, 0.68, 0.52), gDecalCrack * 0.22);
float brickSpallAmt = gDecalSpall * mix(0.88, 1.0, clamp(gDecalDepth, 0.0, 1.0)) * (1.0 - gBrickSkid * 0.65);
diffuseColor.rgb = mix(diffuseColor.rgb, brickSpallMean, brickSpallAmt);
vec3 brickSkidTint = diffuseColor.rgb * vec3(1.18, 1.12, 1.05);
diffuseColor.rgb = mix(diffuseColor.rgb, brickSkidTint, gBrickSkid * 0.82);
vec3 brickRimTint = mix(brickInteriorClay * 1.28, diffuseColor.rgb * vec3(1.24, 1.16, 1.08), 0.3);
diffuseColor.rgb = mix(diffuseColor.rgb, brickRimTint, gDecalRim * 0.95 * gDecalFresh);
diffuseColor.rgb = mix(diffuseColor.rgb, brickInteriorClay * 0.92, gBrickCornerChip * 0.82);
vec3 stairMortarDark = vec3(0.032, 0.026, 0.020);
diffuseColor.rgb *= mix(vec3(1.0), vec3(0.55, 0.53, 0.51), clamp(gBrickStairCrack, 0.0, 1.0));
diffuseColor.rgb = mix(diffuseColor.rgb, stairMortarDark, clamp(gBrickStairHair, 0.0, 1.0));
#else
vec3 brickClay = vec3(0.62, 0.38, 0.27);
diffuseColor.rgb = mix(diffuseColor.rgb, brickClay, gDamageBlotch * mix(0.28, 0.58, clamp(gDamage, 0.0, 1.0)) * bakedFresh * bakeDownLive);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.16, 0.08, 0.05), gDamageCore * 0.82 * bakedFresh * bakeDownLive);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.14, 0.07, 0.05), gDamageCrack * bakedFresh * bakeDownLive);
#endif
#endif
`;
  }

  if (id === 'concrete') {
    const liveGrain =
      opts.face !== 'interior'
        ? `
if (uBakeDownDecals < 0.5) {
float concLiveFp = surfacePixelFootprint(vBakedRestPosition);
float concFineWl = 0.014;
float concFine = surfaceNoiseBL(vBakedRestPosition / concFineWl + vec3(13.0, 2.0, 9.0), uBakedSeed * 19.19, concFineWl, concLiveFp);
float concFine2Wl = 0.007;
float concFine2 = surfaceNoiseBL(vBakedRestPosition / concFine2Wl + vec3(4.0, 17.0, 6.0), uBakedSeed * 19.19, concFine2Wl, concLiveFp);
diffuseColor.rgb *= 1.0
  + (concFine - 0.5) * 0.23 * surfaceOctaveFade(concFineWl, concLiveFp)
  + (concFine2 - 0.5) * 0.14 * surfaceOctaveFade(concFine2Wl, concLiveFp);
vec3 concPitCell = floor(vBakedRestPosition * 3.35 + vec3(4.0, 11.0, 2.0));
vec3 concPitF = fract(vBakedRestPosition * 3.35 + vec3(4.0, 11.0, 2.0));
vec3 concPitO = vec3(
  surfaceHash_(concPitCell, uBakedSeed * 19.19),
  surfaceHash_(concPitCell + vec3(19.0, 7.0, 3.0), uBakedSeed * 19.19),
  surfaceHash_(concPitCell + vec3(5.0, 23.0, 11.0), uBakedSeed * 19.19)
);
float concPitD = length(concPitF - concPitO);
float concPitGate = surfaceNoiseBL(vBakedRestPosition * 0.58 + vec3(9.0, 3.0, 17.0), uBakedSeed * 19.19, 1.72, concLiveFp);
float concPits = (1.0 - smoothstep(0.02, 0.07, concPitD)) * smoothstep(0.66, 0.90, concPitGate) * surfaceOctaveFade(0.012, concLiveFp);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.62, 0.64, 0.66), concPits * 0.72);
}
`
        : '';
    return `#include <color_fragment>
#ifdef USE_DAMAGE_FIELD
fillDamageGlobals(vBakedRestPosition, normalize(vBakedRestNormal));
float bakedFresh = 1.0 - gDamageAge * 0.32;
float bakeDownLive = 1.0 - uBakeDownDecals;
#ifdef USE_DAMAGE_DECAL_ATLAS
sampleDamageDecals(vBakedRestPosition, normalize(vBakedRestNormal));
vec3 concreteBlotchTint = diffuseColor.rgb * vec3(0.86, 0.84, 0.80);
concreteBlotchTint = mix(concreteBlotchTint, mix(concreteBlotchTint, diffuseColor.rgb * 0.92, 0.4), 1.0 - bakedFresh);
diffuseColor.rgb = mix(diffuseColor.rgb, min(diffuseColor.rgb, concreteBlotchTint), gDamageBlotch * 0.03 * bakedFresh * bakeDownLive);
float craterFp = surfacePixelFootprint(vBakedRestPosition);
float craterMeters = max(uBakedTextureScale, 0.05);
vec3 craterP = vBakedRestPosition * (1.0 / craterMeters);
float gConcreteCraterGrain = surfaceNoiseBL(craterP * 8.0 + vec3(5.0, 2.0, 9.0), uBakedSeed * 19.19, craterMeters / 8.0, craterFp) * 0.52
  + surfaceNoiseBL(craterP * 16.0 + vec3(11.0, 4.0, 3.0), uBakedSeed * 19.19, craterMeters / 16.0, craterFp) * 0.28;
vec3 concreteSpallMean = mix(
  diffuseColor.rgb * vec3(0.70, 0.67, 0.62),
  diffuseColor.rgb * vec3(0.48, 0.45, 0.40),
  clamp(gDecalDepth, 0.0, 1.0)
);
concreteSpallMean = mix(concreteSpallMean, mix(concreteSpallMean, diffuseColor.rgb * 0.82, 0.38), 1.0 - gDecalFresh);
float concreteSpallAmt = gDecalSpall * mix(0.82, 1.0, clamp(gDecalDepth, 0.0, 1.0));
vec3 concreteSpallTint = concreteSpallMean * mix(0.86, 1.14, gConcreteCraterGrain);
diffuseColor.rgb = mix(diffuseColor.rgb, concreteSpallTint, concreteSpallAmt);
vec3 concreteRimTint = diffuseColor.rgb * mix(vec3(1.22, 1.18, 1.12), vec3(1.10, 1.08, 1.04), 1.0 - gDecalFresh);
diffuseColor.rgb = mix(diffuseColor.rgb, concreteRimTint, gDecalRim * 0.95);
vec3 concreteCrackTint = diffuseColor.rgb * mix(vec3(0.62, 0.60, 0.58), vec3(0.42, 0.40, 0.38), gDecalCrackCore);
diffuseColor.rgb = mix(diffuseColor.rgb, min(diffuseColor.rgb, concreteCrackTint), gDecalCrack * 0.92);
#else
vec3 concreteBruiseDark = diffuseColor.rgb * vec3(0.45, 0.43, 0.40);
vec3 concreteExposedCore = diffuseColor.rgb * vec3(0.58, 0.55, 0.50);
vec3 concreteBlotchTintB = mix(concreteBruiseDark, concreteExposedCore, mix(0.35, 0.7, clamp(gDamage, 0.0, 1.0)));
diffuseColor.rgb = mix(diffuseColor.rgb, min(diffuseColor.rgb, concreteBlotchTintB), gDamageBlotch * mix(0.4, 0.78, clamp(gDamage, 0.0, 1.0)) * bakedFresh * bakeDownLive);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.28, 0.26, 0.24), gDamageCore * 0.72 * bakedFresh * bakeDownLive);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.14, 0.13, 0.12), gDamageCrack * bakedFresh * bakeDownLive);
#endif
#endif
${liveGrain}
`;
  }

  if (id === 'wood') {
    return `#include <color_fragment>
#ifdef USE_DAMAGE_FIELD
fillDamageGlobals(vBakedRestPosition, normalize(vBakedRestNormal));
float bakedFresh = 1.0 - gDamageAge * 0.32;
float bakeDownLive = 1.0 - uBakeDownDecals;
vec3 woodDamageInterior = vec3(0.831, 0.769, 0.659);
#ifdef USE_DAMAGE_DECAL_ATLAS
sampleWoodDamageDecals(vBakedRestPosition, normalize(vBakedRestNormal));
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.92, 0.88, 0.82), gDamageBlotch * 0.04 * bakedFresh * bakeDownLive);
float woodFp = surfacePixelFootprint(vBakedRestPosition);
float woodMeters = max(uBakedTextureScale, 0.05);
vec3 woodP = vBakedRestPosition * (1.0 / woodMeters);
float gWoodCraterGrain = surfaceNoiseBL(vec3(woodP.x * 3.2, woodP.y * 18.0, woodP.z * 18.0) + vec3(5.0, 2.0, 9.0), uBakedSeed * 17.13, woodMeters / 18.0, woodFp) * 0.48
  + surfaceNoiseBL(vec3(woodP.x * 6.5, woodP.y * 32.0, woodP.z * 32.0) + vec3(11.0, 4.0, 3.0), uBakedSeed * 17.13, woodMeters / 32.0, woodFp) * 0.34
  + surfaceNoiseBL(vec3(woodP.x * 1.6, woodP.y * 8.0, woodP.z * 8.0) + vec3(2.0, 13.0, 7.0), uBakedSeed * 17.13, woodMeters / 8.0, woodFp) * 0.18;
vec3 woodSpallMean = mix(woodDamageInterior * 1.04, woodDamageInterior * 0.78, clamp(gDecalDepth, 0.0, 1.0));
woodSpallMean *= mix(0.80, 1.24, gWoodCraterGrain);
woodSpallMean = mix(woodSpallMean, woodSpallMean * vec3(0.78, 0.62, 0.42), gDecalCrack * 0.28);
float woodSpallAmt = gDecalSpall * mix(0.92, 1.0, clamp(gDecalDepth, 0.0, 1.0));
diffuseColor.rgb = mix(diffuseColor.rgb, woodSpallMean, woodSpallAmt);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.70, 0.55, 0.38), gWoodFurrow * gDecalDepth * 0.40);
vec3 woodRimTint = mix(woodDamageInterior * 1.22, diffuseColor.rgb * vec3(1.20, 1.14, 1.04), 0.3);
diffuseColor.rgb = mix(diffuseColor.rgb, woodRimTint, gDecalRim * 0.95 * gDecalFresh);
vec3 woodCrackTint = diffuseColor.rgb * mix(vec3(0.70, 0.58, 0.42), vec3(0.48, 0.36, 0.24), gDecalCrackCore);
diffuseColor.rgb = mix(diffuseColor.rgb, min(diffuseColor.rgb, woodCrackTint), gDecalCrack * 0.88);
#else
diffuseColor.rgb = mix(diffuseColor.rgb, woodDamageInterior, gDamageBlotch * mix(0.30, 0.64, clamp(gDamage, 0.0, 1.0)) * bakedFresh * bakeDownLive);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.16, 0.08, 0.04), gDamageCore * 0.78 * bakedFresh * bakeDownLive);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * 0.28, gDamageCrack * bakedFresh * bakeDownLive);
#endif
#endif
`;
  }

  if (id === 'stone') {
    return `#include <color_fragment>
#ifdef USE_DAMAGE_FIELD
fillDamageGlobals(vBakedRestPosition, normalize(vBakedRestNormal));
float bakedFresh = 1.0 - gDamageAge * 0.32;
float bakeDownLive = 1.0 - uBakeDownDecals;
vec3 stoneDamageInterior = vec3(0.604, 0.627, 0.659);
#ifdef USE_DAMAGE_DECAL_ATLAS
bakedStoneGrid(vBakedRestPosition, normalize(vBakedRestNormal));
sampleStoneDamageDecals(
  vBakedRestPosition,
  normalize(vBakedRestNormal),
  uBakedTextureScale,
  gStoneJoint,
  gStoneEdgeX,
  gStoneEdgeY,
  gStoneRow,
  gStoneCol,
  gStoneFaceX,
  gStoneFaceY,
  gStoneBlockW,
  gStoneCourseH
);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.90, 0.90, 0.88), gDamageBlotch * 0.04 * bakedFresh * bakeDownLive);
float stoneFp = surfacePixelFootprint(vBakedRestPosition);
float stoneMeters = max(uBakedTextureScale, 0.05);
vec3 stoneP = vBakedRestPosition * (1.0 / stoneMeters);
// Coarse octave first: the 18x/36x octaves (2.5-5cm) band-limit to their 0.5
// mean at gameplay distance and left chips flat white. ~15cm survives always.
// (Mirror of the StoneMaterial.ts live-path fix — keep both in sync.)
float gStoneCraterGrain = surfaceNoiseBL(stoneP * 6.0 + vec3(7.0, 13.0, 2.0), uBakedSeed * 13.17, stoneMeters / 6.0, stoneFp) * 0.45
  + surfaceNoiseBL(stoneP * 18.0 + vec3(5.0, 2.0, 9.0), uBakedSeed * 13.17, stoneMeters / 18.0, stoneFp) * 0.30
  + surfaceNoiseBL(stoneP * 36.0 + vec3(11.0, 4.0, 3.0), uBakedSeed * 13.17, stoneMeters / 36.0, stoneFp) * 0.25;
float gStoneChipHash = surfaceHash_(floor(stoneP * 28.0), uBakedSeed * 13.17);
gStoneCraterGrain = mix(gStoneCraterGrain, mix(0.22, 0.82, gStoneChipHash), 0.38);
vec3 stoneSpallMean = mix(stoneDamageInterior * 1.02, stoneDamageInterior * 0.62, clamp(gDecalDepth * 1.6, 0.0, 1.0));
stoneSpallMean *= mix(0.74, 1.26, gStoneCraterGrain);
stoneSpallMean = mix(stoneSpallMean, stoneSpallMean * vec3(0.86, 0.86, 0.84), gDecalCrack * 0.22);
float stoneSpallAmt = gDecalSpall * mix(0.88, 1.0, clamp(gDecalDepth, 0.0, 1.0)) * (1.0 - gStoneSkid * 0.22);
diffuseColor.rgb = mix(diffuseColor.rgb, stoneSpallMean, stoneSpallAmt);
vec3 stoneSkidTint = mix(stoneDamageInterior * 1.06, diffuseColor.rgb * vec3(1.18, 1.14, 1.08), 0.25);
diffuseColor.rgb = mix(diffuseColor.rgb, stoneSkidTint, gStoneSkid * 0.90);
vec3 stoneRimTint = mix(stoneDamageInterior * 1.24, diffuseColor.rgb * vec3(1.20, 1.16, 1.10), 0.3);
diffuseColor.rgb = mix(diffuseColor.rgb, stoneRimTint, gDecalRim * 0.95 * gDecalFresh);
vec3 stoneJointDark = vec3(0.070, 0.075, 0.080);
diffuseColor.rgb *= mix(vec3(1.0), vec3(0.58, 0.58, 0.56), clamp(gStoneJointCrack, 0.0, 1.0));
diffuseColor.rgb = mix(diffuseColor.rgb, stoneJointDark, clamp(gStoneJointHair, 0.0, 1.0));
#else
diffuseColor.rgb = mix(diffuseColor.rgb, stoneDamageInterior, gDamageBlotch * mix(0.28, 0.58, clamp(gDamage, 0.0, 1.0)) * bakedFresh * bakeDownLive);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.14, 0.15, 0.16), gDamageCore * 0.82 * bakedFresh * bakeDownLive);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.14, 0.15, 0.16), gDamageCrack * bakedFresh * bakeDownLive);
#endif
#endif
`;
  }

  if (id === 'plaster') {
    return `#include <color_fragment>
#ifdef USE_DAMAGE_FIELD
fillDamageGlobals(vBakedRestPosition, normalize(vBakedRestNormal));
float bakedFresh = 1.0 - gDamageAge * 0.32;
float bakeDownLive = 1.0 - uBakeDownDecals;
vec3 plasterDamageInterior = vec3(0.937, 0.910, 0.863);
diffuseColor.rgb = mix(diffuseColor.rgb, plasterDamageInterior, gDamageBlotch * mix(0.28, 0.58, clamp(gDamage, 0.0, 1.0)) * bakedFresh * bakeDownLive);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.18, 0.16, 0.14), gDamageCore * 0.78 * bakedFresh * bakeDownLive);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.16, 0.14, 0.12), gDamageCrack * bakedFresh * bakeDownLive);
#endif
`;
  }

  return `#include <color_fragment>
#ifdef USE_DAMAGE_FIELD
fillDamageGlobals(vBakedRestPosition, normalize(vBakedRestNormal));
float bakedFresh = 1.0 - gDamageAge * 0.32;
float bakeDownLive = 1.0 - uBakeDownDecals;
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.55, 0.52, 0.48), gDamageBlotch * mix(0.28, 0.58, clamp(gDamage, 0.0, 1.0)) * bakedFresh * bakeDownLive);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * 0.22, gDamageCrack * bakedFresh * bakeDownLive);
diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.16, 0.14, 0.12), gDamageCore * 0.7 * bakedFresh * bakeDownLive);
#endif
`;
}

export function bakedDamageRoughnessSnippet(opts: BakedDamageHookOpts): string {
  const face = opts.face ?? 'exterior';
  const id = opts.profileId;
  if (face === 'interior' || !id) {
    return `#include <roughnessmap_fragment>
#ifdef USE_DAMAGE_FIELD
float bakeDownLiveR = 1.0 - uBakeDownDecals;
roughnessFactor = clamp(roughnessFactor + (gDamageBlotch * 0.07 + gDamageCrack * 0.08) * bakeDownLiveR, 0.04, 1.0);
#endif
`;
  }
  if (id === 'brick') {
    return `#include <roughnessmap_fragment>
#ifdef USE_DAMAGE_FIELD
float bakeDownLiveR = 1.0 - uBakeDownDecals;
#ifdef USE_DAMAGE_DECAL_ATLAS
roughnessFactor = clamp(
  roughnessFactor
    + gDamageBlotch * 0.03 * bakeDownLiveR
    + gDecalSpall * 0.12
    + gDecalDepth * 0.08
    + gBrickStairCrack * 0.10
    + gBrickStairHair * 0.06
    + gBrickSkid * 0.05
    + gBrickCornerChip * 0.04,
  0.18,
  0.98
);
#else
roughnessFactor = clamp(roughnessFactor + (gDamageBlotch * 0.07 + gDamageCrack * 0.08) * bakeDownLiveR, 0.18, 0.98);
#endif
#endif
`;
  }
  if (id === 'concrete') {
    return `#include <roughnessmap_fragment>
#ifdef USE_DAMAGE_FIELD
float bakeDownLiveR = 1.0 - uBakeDownDecals;
#ifdef USE_DAMAGE_DECAL_ATLAS
roughnessFactor = clamp(
  roughnessFactor
    + gDamageBlotch * 0.03 * bakeDownLiveR
    + gDecalSpall * 0.12
    + gDecalDepth * 0.10
    + gDecalCrack * 0.08,
  0.04,
  1.0
);
#else
roughnessFactor = clamp(roughnessFactor + (gDamageBlotch * 0.07 + gDamageCrack * 0.08) * bakeDownLiveR, 0.04, 1.0);
#endif
#endif
`;
  }
  if (id === 'stone') {
    return `#include <roughnessmap_fragment>
#ifdef USE_DAMAGE_FIELD
float bakeDownLiveR = 1.0 - uBakeDownDecals;
#ifdef USE_DAMAGE_DECAL_ATLAS
roughnessFactor = clamp(
  roughnessFactor
    + gDamageBlotch * 0.03 * bakeDownLiveR
    + gDecalSpall * 0.12
    + gDecalDepth * 0.08
    + gStoneJointCrack * 0.10
    + gStoneJointHair * 0.06
    + gStoneSkid * 0.05
    + gStoneCornerChip * 0.04,
  0.18,
  0.98
);
#else
roughnessFactor = clamp(roughnessFactor + (gDamageBlotch * 0.07 + gDamageCrack * 0.08) * bakeDownLiveR, 0.18, 0.98);
#endif
#endif
`;
  }
  if (id === 'wood') {
    return `#include <roughnessmap_fragment>
#ifdef USE_DAMAGE_FIELD
float bakeDownLiveR = 1.0 - uBakeDownDecals;
#ifdef USE_DAMAGE_DECAL_ATLAS
roughnessFactor = clamp(
  roughnessFactor
    + gDamageBlotch * 0.03 * bakeDownLiveR
    + gDecalSpall * 0.12
    + gDecalDepth * 0.10
    + gDecalCrack * 0.08
    + gWoodFurrow * 0.06,
  0.12,
  0.96
);
#else
roughnessFactor = clamp(roughnessFactor + (gDamageBlotch * 0.07 + gDamageCrack * 0.08) * bakeDownLiveR, 0.12, 0.96);
#endif
#endif
`;
  }
  return `#include <roughnessmap_fragment>
#ifdef USE_DAMAGE_FIELD
float bakeDownLiveR = 1.0 - uBakeDownDecals;
roughnessFactor = clamp(roughnessFactor + (gDamageBlotch * 0.07 + gDamageCrack * 0.08) * bakeDownLiveR, 0.04, 1.0);
#endif
`;
}

export function bakedDamageAoSnippet(opts: BakedDamageHookOpts): string {
  const atlas =
    opts.face !== 'interior'
    && (opts.profileId === 'brick'
      || opts.profileId === 'concrete'
      || opts.profileId === 'stone'
      || opts.profileId === 'wood');
  if (!atlas) {
    return `#include <aomap_fragment>`;
  }
  return `#include <aomap_fragment>
#ifdef USE_DAMAGE_FIELD
#ifdef USE_DAMAGE_DECAL_ATLAS
reflectedLight.indirectDiffuse *= 1.0 - gDecalAo * 0.42 - gDecalDepth * 0.10;
#endif
#endif
`;
}
